import { error, find, instantiate, Layers, Node, NodePool, Prefab, _decorator } from 'cc';
import MessageDialog from './Toolkit/Components/Dialog/MessageDialog';
import Page from './Toolkit/Components/Page/Page';
import { ErrorEntity, ErrorType } from './ErrorHandler/ErrorHandler';
import GameDependency from './System/GameDependency';
import Dialog from './Toolkit/Components/Dialog/Dialog';

enum EventType {
    MESSAGE_SHOW = 'MESSAGE_SHOW',
}

export enum MessageType {
    ERROR = 'Error',
    MESSAGE = 'Message',
}

const { ccclass, property } = _decorator;

@ccclass('MessageManager')
export default class MessageManager extends GameDependency {
    public static readonly EVENT_TYPE = EventType;

    protected static _hasFatalShown = false;
    protected static _lastFatalError: ErrorEntity = null;

    @property(Prefab)
    protected readonly messageDialogPrefab: Prefab = null;

    @property(Node)
    protected readonly presetRoot: Node = null;

    protected pool: NodePool = new NodePool();
    private _root: Node = null;
    private _currentDisplayErrorEntities: ErrorEntity[] = [];
    private _errorDialogs: { [code: string]: MessageDialog } = {};

    public static get hasFatalShown() {
        return this._hasFatalShown;
    }

    public static get lastFatalError() {
        return this._lastFatalError;
    }

    public get currentDisplayErrorEntities() {
        return this._currentDisplayErrorEntities;
    }

    public get hasFatalShown() {
        return MessageManager._hasFatalShown;
    }

    protected set hasFatalShown(value: boolean) {
        MessageManager._hasFatalShown = value;
    }

    public get root(): Node {
        if (!this._root) {
            if (this.presetRoot) {
                this._root = this.presetRoot;
            } else {
                const node = new Node('MessageDialogRoot');
                node.layer = Layers.Enum.UI_2D;
                find('Canvas').addChild(node);
                this._root = node;
            }
        }
        return this._root;
    }

    public get lastFatalError() {
        return MessageManager._lastFatalError;
    }

    protected set lastFatalError(value: ErrorEntity) {
        MessageManager._lastFatalError = value;
    }

    protected get errorDialogs() {
        return this._errorDialogs;
    }

    public onLoad() {
        if (this.game.message) {
            this.destroy();
            error('No duplicate MessageManager');
            return;
        }
        this.game.message = this;
    }

    public start() {
        if (this.lastFatalError) {
            this.hasFatalShown = false;
            this.showError(this.lastFatalError);
        }
    }

    public onDestroy() {
        this.game.message = null;
    }

    public async showError(errorEntity: ErrorEntity) {
        // can't open when displaying fatal error message
        if (this.hasFatalShown) {
            return null;
        }
        // don't show the same error while it's displaying
        if (this._currentDisplayErrorEntities.includes(errorEntity)) {
            return this._errorDialogs[errorEntity.name];
        }

        const messageType = errorEntity.type === ErrorType.Error ? MessageType.ERROR : MessageType.MESSAGE;
        if (errorEntity.type === ErrorType.Error) {
            this.initFatalError(errorEntity);
        }

        const dialog = await this.getMessageDialog();
        this._currentDisplayErrorEntities.push(errorEntity);
        this.display(dialog, messageType, messageType, errorEntity.name, errorEntity.code);
        this._errorDialogs[errorEntity.name] = dialog;
        dialog.once(Page.EVENT_TYPE.CLOSE_END, () => {
            const index = this._currentDisplayErrorEntities.indexOf(errorEntity);
            if (index !== -1) {
                this._currentDisplayErrorEntities.splice(index, 1);
            }
        });
        return dialog;
    }

    public async showMessage(title: string, message: string, cancelable = false, playSoundOnOpen = true, closeOnBlur = false) {
        // can't open when displaying fatal error message
        if (this.hasFatalShown) {
            return null;
        }
        const dialog = await this.getMessageDialog();
        dialog.getComponent(Page).closeOnBlur = closeOnBlur;
        this.display(dialog, MessageType.MESSAGE, title, message, MessageType.MESSAGE, cancelable, playSoundOnOpen);
        return dialog;
    }

    public closeErrorMessages(errorNames: string[]) {
        errorNames.forEach((name) => {
            const dialog = this._errorDialogs[name];
            if (dialog) {
                dialog.forceClose();
                this._errorDialogs[name] = null;
            }
        });
    }

    protected display(dialog: MessageDialog, type: MessageType, ...args: Parameters<MessageDialog['display']>) {
        dialog.display(...args);
        this.emit(MessageManager.EVENT_TYPE.MESSAGE_SHOW, type, dialog, args);
    }

    protected async getMessageDialog() {
        const node = this.pool.get() ?? (await this.spawnMessageDialog());
        this.root.addChild(node);
        return node.getComponent(MessageDialog);
    }

    protected async spawnMessageDialog() {
        const node = instantiate(this.messageDialogPrefab);
        node.on(Page.EVENT_TYPE.CLOSE_END, () => {
            this.clearDialogEvents(node);
            this.pool.put(node);
        });
        return node;
    }

    protected clearDialogEvents(dialog: Node) {
        dialog.off(Dialog.EVENT_TYPE.CONFIRM);
        dialog.off(Dialog.EVENT_TYPE.CANCEL);
    }

    protected initFatalError(errorEntity: ErrorEntity) {
        this.hasFatalShown = true;
        this.lastFatalError = errorEntity;
        // hide all other dialogs
        this.root.children.forEach((node) => node.getComponent(Page).close());
    }
}
